import React from 'react';
import { Link } from 'react-router-dom';
import styles from './Account.module.css';
import { IconChevron, IconExternal } from './AccountIcons';

/**
 * One band of the account hub.
 *
 * The eyebrow carries the section's rank in the hierarchy (identity, then the
 * public card, then preferences, then security) so the page reads as an
 * ordered document rather than an undifferentiated pile of cards.
 */
export function Section({ id, eyebrow, title, description, actions, children, tone }) {
  return (
    <section
      className={[styles.section, tone === 'danger' ? styles.sectionDanger : ''].filter(Boolean).join(' ')}
      aria-labelledby={`${id}-title`}
    >
      <header className={styles.sectionHead}>
        <div className={styles.sectionHeadText}>
          {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
          <h2 className={styles.sectionTitle} id={`${id}-title`}>
            {title}
          </h2>
          {description ? <p className={styles.sectionDescription}>{description}</p> : null}
        </div>
        {actions ? <div className={styles.sectionActions}>{actions}</div> : null}
      </header>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

/** A row that goes somewhere. Renders as a real link when `to` is an in-app
 *  route so it can be opened in a new tab, and as a button otherwise. */
export function NavRow({ icon, label, hint, to, href, onClick, status, tone, disabled }) {
  const className = [styles.row, styles.rowInteractive, tone === 'danger' ? styles.rowDanger : '']
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      {icon ? (
        <span className={styles.rowIcon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className={styles.rowMain}>
        <span className={styles.rowLabel}>{label}</span>
        {hint ? <span className={styles.rowHint}>{hint}</span> : null}
      </span>
      {status ? <span className={styles.rowStatus}>{status}</span> : null}
      <span className={styles.rowChevron} aria-hidden="true">
        {href ? <IconExternal size={16} /> : <IconChevron size={16} />}
      </span>
    </>
  );

  if (href) {
    return (
      <a className={className} href={href} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }

  if (to) {
    return (
      <Link className={className} to={to}>
        {inner}
      </Link>
    );
  }

  return (
    <button type="button" className={className} onClick={onClick} disabled={disabled}>
      {inner}
    </button>
  );
}

/** A label/value pair. Static by default; `children` replaces the value with
 *  a control when the section is in edit mode. */
export function InfoRow({ label, value, empty = 'Not set', children, htmlFor }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowMain}>
        {htmlFor ? (
          <label className={styles.rowLabelMuted} htmlFor={htmlFor}>
            {label}
          </label>
        ) : (
          <span className={styles.rowLabelMuted}>{label}</span>
        )}
        {children ? (
          <span className={styles.rowControl}>{children}</span>
        ) : (
          <span className={value ? styles.rowValue : styles.rowValueEmpty}>{value || empty}</span>
        )}
      </span>
    </div>
  );
}

export function Pill({ children, tone }) {
  return (
    <span
      className={[
        styles.pill,
        tone === 'positive' ? styles.pillPositive : '',
        tone === 'muted' ? styles.pillMuted : '',
        tone === 'warning' ? styles.pillWarning : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </span>
  );
}

export function Skeleton({ width = '100%', height = 12, radius = 8 }) {
  return (
    <span
      className={styles.skeleton}
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

export function Notice({ tone = 'error', children, onDismiss }) {
  if (!children) return null;
  return (
    <div
      className={[styles.notice, tone === 'success' ? styles.noticeSuccess : styles.noticeError].join(' ')}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      <span>{children}</span>
      {onDismiss ? (
        <button type="button" className={styles.noticeDismiss} onClick={onDismiss} aria-label="Dismiss message">
          ×
        </button>
      ) : null}
    </div>
  );
}
